/**
 * 初回OCR値と現在値を比べて、ユーザーが値を直した項目を洗い出す（#2967）。
 *
 * 子パターンの取得ルール提案は、**直した項目だけ**を対象にする。
 * 親パターンのルールを改善しても子パターンには伝播しないため、
 * 上書きが多いほど二重管理になり、メンテナンスが重くなるため。
 *
 * 明細（array項目）は**列単位**で判定する。取得ルールは列に対する設定なので、
 * どのセルを直したかまで区別しても、結局その列のルールを直すことになる。
 *
 * 数文字だけ直した項目（誤読）は対象にしない（#3380）。子パターンで直せるのは
 * 「別の場所から値を拾っている」項目で、読む場所が合っている誤読は直らないため。
 *
 * 明細の行数を直した場合は、列ではなく明細の親項目を対象にする（#3380）。
 * 行の増減は表の範囲の読み違いで、直すべきは表の読み方（親項目のルール）だから。
 */

import dayjs from 'dayjs';
import { parseDateStringSafely } from './fileExportUtils';
import { countChangedChars } from './accuracyCalc';
import {
  SUB_PATTERN_MISREAD_DIFF_RATIO,
  SUB_PATTERN_MISREAD_MIN_LENGTH,
  SUB_PATTERN_ROW_SAMPLE_COUNT,
} from '../../constants/enum';

/** 比較に使う項目定義。item_list の必要な部分だけを受け取る */
export interface ChangedItemCandidate {
  id: string;
  children?: Array<{ id: string }>;
}

/** 絞り込みに使う項目定義。item_list の必要な部分だけを受け取る */
export interface SuggestableItemCandidate {
  id: string;
  is_active?: boolean;
  is_ocr_target?: boolean;
  children?: Array<{ id: string; is_active?: boolean; is_ocr_target?: boolean }>;
}

/**
 * 取得ルールの提案の対象になりうる項目だけに絞る（#3314）。
 *
 * AI読取の対象外（手入力欄）と無効化された項目は、取得ルールを書いても効かないため
 * 値の比較対象にしない。手入力した項目は当然この値と初回OCR値が食い違うが、
 * それは「AIの読み取りが外れた」のではなく「人が入力した」だけで、提案の材料にならない。
 *
 * 全列がAI読取OFFの明細は列が1つも残らない。そのまま渡すと
 * collectChangedItemIds が「明細ではない項目」と誤認し、行の配列同士を
 * 文字列として比較してしまうため、明細ごと対象から外す。
 */
export const filterSuggestableItems = (
  itemList: SuggestableItemCandidate[],
): ChangedItemCandidate[] => {
  const isSuggestable = (item: { is_active?: boolean; is_ocr_target?: boolean }): boolean =>
    item.is_active !== false && item.is_ocr_target !== false;

  return itemList
    .filter(isSuggestable)
    .map(item => ({
      id: item.id,
      isArray: (item.children ?? []).length > 0,
      children: (item.children ?? []).filter(isSuggestable).map(child => ({ id: child.id })),
    }))
    .filter(item => !item.isArray || item.children.length > 0)
    .map(({ id, children }) => ({ id, children }));
};

/**
 * 値を比較用の文字列に正規化する。
 *
 * 日付は表記揺れ（Timestamp / 文字列）を吸収し、数値は桁区切りと小数の
 * 末尾ゼロを吸収する（2630.0 と 2630 は同一、26.30 と 2630 は別扱い）。
 */
const normalizeValue = (val: unknown): string => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'object' && 'seconds' in (val as object)) {
    return dayjs(new Date((val as { seconds: number }).seconds * 1000)).format('YYYY/MM/DD');
  }
  if (typeof val === 'string') {
    // 数値の判定を先に行う。'2630.0' のような値は dayjs が「2630年1月1日」と
    // 解釈してしまうため、日付を先に見ると金額が日付に化ける
    const numVal = parseFloat(val.replace(/,/g, ''));
    if (!isNaN(numVal) && /^-?[\d,]+\.?\d*$/.test(val.trim())) {
      return String(numVal);
    }
    const parsed = parseDateStringSafely(val);
    if (parsed) return parsed.format('YYYY/MM/DD');
  }
  if (typeof val === 'number') return String(val);
  return String(val);
};

/** 項目の値は `{ value: ... }` で包まれていることがあるため、中身を取り出して正規化する */
const normalizeFieldValue = (fieldData: unknown): string => {
  if (fieldData === null || fieldData === undefined) return '';
  if (typeof fieldData === 'object' && 'value' in (fieldData as object)) {
    return normalizeValue((fieldData as Record<string, unknown>).value);
  }
  return normalizeValue(fieldData);
};

/**
 * 直す前と後の差が誤読程度か（#3380）。
 *
 * 末尾の0の欠けや数桁中1桁の違いは、読む場所は合っていて罫線との重なりや
 * 手書きで読み違えただけ。子パターンで場所を指定しても直らないため提案しない。
 * 全く違う値に直した場合は、別の場所から拾っていると考えて提案する。
 *
 * 片方が空の場合は誤読ではない（取れなかった・取るべきでない値を取った）。
 */
const isMisread = (before: string, after: string): boolean => {
  if (before === '' || after === '') return false;
  const longerLength = Math.max(before.length, after.length);
  if (longerLength < SUB_PATTERN_MISREAD_MIN_LENGTH) return false;
  const allowedDiff = Math.max(1, Math.floor(longerLength * SUB_PATTERN_MISREAD_DIFF_RATIO));
  return countChangedChars(before, after) <= allowedDiff;
};

/** 値を別の値に直したか。表記揺れと誤読程度の差は直したとみなさない */
const isRelocated = (beforeField: unknown, afterField: unknown): boolean => {
  const before = normalizeFieldValue(beforeField);
  const after = normalizeFieldValue(afterField);
  return before !== after && !isMisread(before, after);
};

/**
 * 値が変更された項目のIDを返す。
 *
 * 返すIDは item_list と同じ形式（親項目は `"2"`、明細の列は `"2-1"`）。
 * 誤読程度の差（isMisread）は変更とみなさない（#3380）。
 * 明細は列ごとに判定し、**その列のどこか1セルでも違えば**列のIDを返す。
 * 行数が変わった場合は、列ではなく明細の親項目のIDを返す（#3380）。
 * 行の増減は表の範囲の読み違いで、各列の位置は合っていることがほとんど。
 * 全列を返すと列ごとに上書きが増え、二重管理になる。
 *
 * 履歴が無い・比較できない場合は空配列を返す（呼び出し側で「対象なし」として扱う）。
 */
export const collectChangedItemIds = (
  initialResultData: Record<string, unknown> | null | undefined,
  currentResultData: Record<string, unknown> | null | undefined,
  itemList: ChangedItemCandidate[],
): string[] => {
  if (!initialResultData || !currentResultData) return [];

  const changed: string[] = [];

  for (const item of itemList) {
    const children = item.children ?? [];
    const isArrayField = children.length > 0;

    if (!isArrayField) {
      if (isRelocated(initialResultData[item.id], currentResultData[item.id])) {
        changed.push(item.id);
      }
      continue;
    }

    const initialRows = (initialResultData[item.id] as Array<Record<string, unknown>>) ?? [];
    const currentRows = (currentResultData[item.id] as Array<Record<string, unknown>>) ?? [];

    // 行数が変われば表の範囲の読み方がずれている。列ではなく表の読み方（親）を対象にする。
    // 行がずれるため、残った行同士のセル比較もしない
    if (initialRows.length !== currentRows.length) {
      changed.push(item.id);
      continue;
    }

    for (const child of children) {
      const hasDiff = initialRows.some((row, index) =>
        isRelocated(row[child.id], currentRows[index]?.[child.id]),
      );
      if (hasDiff) changed.push(child.id);
    }
  }

  return changed;
};

/** 行数を直した明細の、直す前後の状態（#3380） */
export interface RowCountChange {
  beforeRowCount: number;
  afterRowCount: number;
  /** 直す前に読み取っていた先頭数行。キーは列名（子項目名） */
  beforeSampleRows: Array<Record<string, string>>;
}

/** 行数の比較に使う項目定義。サンプル行のキーに列名を使うため子項目名も受け取る */
export interface RowCountChangeCandidate {
  id: string;
  children?: Array<{ id: string; childValue: string }>;
}

/**
 * 行数を直した明細について、直す前に読み取っていた行を集める（#3380）。
 *
 * AIに「何を明細として誤って拾っていたか」を伝えるために使う。これが無いと
 * AIは帳票画像から正しい明細表を探し、その位置を書いてしまう（拾った範囲を除外できない）。
 *
 * @returns 明細の親項目ID → 直す前後の状態。行数が変わっていない明細は含まない
 */
export const collectRowCountChanges = (
  initialResultData: Record<string, unknown> | null | undefined,
  currentResultData: Record<string, unknown> | null | undefined,
  itemList: RowCountChangeCandidate[],
): Record<string, RowCountChange> => {
  if (!initialResultData || !currentResultData) return {};

  const changes: Record<string, RowCountChange> = {};
  for (const item of itemList) {
    const children = item.children ?? [];
    if (children.length === 0) continue;

    const initialRows = (initialResultData[item.id] as Array<Record<string, unknown>>) ?? [];
    const currentRows = (currentResultData[item.id] as Array<Record<string, unknown>>) ?? [];
    if (initialRows.length === currentRows.length) continue;

    changes[item.id] = {
      beforeRowCount: initialRows.length,
      afterRowCount: currentRows.length,
      beforeSampleRows: initialRows
        .slice(0, SUB_PATTERN_ROW_SAMPLE_COUNT)
        .map(row =>
          Object.fromEntries(
            children.map(child => [child.childValue, normalizeFieldValue(row?.[child.id])]),
          ),
        ),
    };
  }
  return changes;
};
